import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../api";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    age: "",
    gender: "female",
    seeking: "male",
    location: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const age = parseInt(form.age, 10);
    if (isNaN(age) || age < 40) {
      setError("Veritas40+ is exclusively for members aged 40 and above.");
      return;
    }
    setBusy(true);
    try {
      await register({ ...form, age });
      toast.success("Account created. Complete your profile, then book your video verification.");
      navigate("/profile");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#E7DFD5] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A34]/30";

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 texture-grain">
      <div className="card-surface w-full max-w-lg p-8" data-testid="register-form">
        <p className="eyebrow mb-2">Join the verified community</p>
        <h1 className="font-display text-3xl text-stone-900 mb-2">Create your account</h1>
        <p className="text-sm text-stone-500 mb-6 flex items-center gap-2">
          <ShieldCheck size={14} className="text-[#065F46]" />
          You'll book a short video interview after this — it's how we keep everyone safe.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">Full name</label>
              <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" data-testid="register-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">Age (40+)</label>
              <input required type="number" min="40" max="100" value={form.age} onChange={set("age")} className={inputCls} placeholder="45" data-testid="register-age-input" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-stone-700 block mb-1.5">Email</label>
            <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="register-email-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-stone-700 block mb-1.5">Password (min 8 characters)</label>
            <input required type="password" minLength="8" value={form.password} onChange={set("password")} className={inputCls} placeholder="••••••••" data-testid="register-password-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">I am</label>
              <select value={form.gender} onChange={set("gender")} className={inputCls} data-testid="register-gender-select">
                <option value="female">A woman</option>
                <option value="male">A man</option>
                <option value="other">Non-binary / other</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">Looking for</label>
              <select value={form.seeking} onChange={set("seeking")} className={inputCls} data-testid="register-seeking-select">
                <option value="male">Men</option>
                <option value="female">Women</option>
                <option value="everyone">Everyone</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-stone-700 block mb-1.5">City, State</label>
            <input required value={form.location} onChange={set("location")} className={inputCls} placeholder="Portland, OR" data-testid="register-location-input" />
          </div>
          {error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="register-error-message">
              {error}
            </p>
          )}
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="register-form-submit-button">
            {busy ? "Creating account…" : "Create account"}
          </button>
        </form>
        <p className="mt-6 text-sm text-stone-500 text-center">
          Already a member?{" "}
          <Link to="/login" className="text-[#C58342] font-medium hover:underline" data-testid="register-login-link">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
